/**
 * Portal knowledge base — Step 33. Browse (categories → folders) and the
 * article page with "was this helpful?" voting, matching the real portal's
 * shape (streetkidsdirect.freshdesk.com is knowledge-base-centric: browse
 * articles is one of its two primary actions).
 *
 * Only folders this requester may see ever reach the response, and drafts
 * never do — both decided server-side in lib/kbVisibility.ts.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, FolderOpen, ThumbsDown, ThumbsUp } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { ArticleBody } from '../../features/kb/ArticleBody';
import { qk } from '../../lib/queryClient';
import { relativeTime } from '../../lib/time';

export function PortalKbPage() {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalKbTree,
    queryFn: portalApi.kbTree,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold">Help articles</h1>
        <p className="text-xs text-muted-foreground">Look up guides and how-tos to fix things on your own.</p>
      </div>

      {isError && (
        <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">Could not load the help articles</p>
          <p className="text-xs text-destructive/80">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="text-xs text-destructive/80 underline hover:text-destructive"
          >
            Retry
          </button>
        </div>
      )}
      {isPending && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {data &&
        (data.categories.length === 0 ? (
          <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
            No help articles have been published yet.
          </div>
        ) : (
          data.categories.map((cat) => (
            <section key={cat.id}>
              <h2 className="mb-2 text-sm font-semibold text-muted-foreground">{cat.name}</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {cat.folders.map((f) => (
                  <Link
                    key={f.id}
                    to={`/portal/help/folder/${f.id}`}
                    className="group rounded-lg border bg-card p-4 transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-md bg-brand/10 text-brand">
                      <FolderOpen className="h-5 w-5" />
                    </span>
                    <p className="text-sm font-semibold group-hover:text-brand">{f.name}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {f.published} article{f.published === 1 ? '' : 's'}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          ))
        ))}
    </div>
  );
}

/** A folder's published articles. The list comes from the same tree call —
 *  the portal never asks for articles by folder id directly, so a folder the
 *  viewer cannot see simply is not in the tree to link to. */
export function PortalKbFolderPage() {
  const { folderId } = useParams<{ folderId: string }>();
  const { data, isPending } = useQuery({ queryKey: qk.portalKbTree, queryFn: portalApi.kbTree });
  if (isPending) return <Skeleton className="h-64 w-full" />;
  const folder = data?.categories.flatMap((c) => c.folders).find((f) => f.id === folderId);
  if (!folder)
    return (
      <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
        That folder is not available.
      </div>
    );

  return (
    <div className="space-y-4">
      <Link to="/portal/help" className="flex w-fit items-center gap-1.5 text-sm text-brand hover:underline">
        <ArrowLeft className="h-4 w-4" /> All help articles
      </Link>
      <h1 className="text-lg font-semibold">{folder.name}</h1>
      <FolderArticles folderId={folder.id} />
    </div>
  );
}

/** Articles in one folder. The endpoint re-checks the folder's visibility
 *  tier server-side, so linking here can never widen what this viewer sees. */
function FolderArticles({ folderId }: { folderId: string }) {
  const { data, isPending } = useQuery({
    queryKey: qk.kbFolderArticles(folderId),
    queryFn: () => portalApi.kbFolderArticles(folderId),
  });
  if (isPending) return <Skeleton className="h-40 w-full" />;
  if (!data || data.entries.length === 0)
    return (
      <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
        Nothing published in this folder yet.
      </div>
    );
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {data.entries.map((a) => (
        <Link
          key={a.id}
          to={`/portal/help/${a.slug}`}
          className="flex items-center gap-2.5 border-b px-4 py-3 text-sm last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{a.title}</span>
        </Link>
      ))}
    </div>
  );
}

export function PortalKbArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const [voted, setVoted] = useState<boolean | null>(null);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.portalKbArticle(slug ?? ''),
    queryFn: () => portalApi.kbArticle(slug!),
    enabled: !!slug,
  });
  const vote = useMutation({
    mutationFn: (helpful: boolean) => portalApi.kbVote(slug!, helpful),
    onSuccess: (_res, helpful) => {
      setVoted(helpful);
      void queryClient.invalidateQueries({ queryKey: qk.portalKbArticle(slug ?? '') });
    },
  });

  if (isPending) return <Skeleton className="h-96 w-full" />;
  if (isError)
    return (
      <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load that article</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  return (
    <article className="w-full">
      <Link
        to="/portal/help"
        className="mb-4 flex w-fit items-center gap-1.5 text-sm text-brand hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> All help articles
      </Link>
      <div className="rounded-lg border bg-card p-6">
        <p className="mb-1 text-xs text-muted-foreground">{data!.folderName}</p>
        <h1 className="text-xl font-semibold">{data!.title}</h1>
        <p className="mt-1 text-xs text-muted-foreground">Updated {relativeTime(data!.updatedAt)}</p>
        <ArticleBody html={data!.bodyHtml} className="mt-5" />
        <div className="mt-6 flex flex-wrap items-center gap-3 border-t pt-4">
          <span className="text-sm text-muted-foreground">Was this helpful?</span>
          <Button
            variant={voted === true ? 'default' : 'outline'}
            size="sm"
            disabled={vote.isPending}
            onClick={() => vote.mutate(true)}
          >
            <ThumbsUp /> Yes
          </Button>
          <Button
            variant={voted === false ? 'default' : 'outline'}
            size="sm"
            disabled={vote.isPending}
            onClick={() => vote.mutate(false)}
          >
            <ThumbsDown /> No
          </Button>
          {voted !== null && <span className="text-xs text-muted-foreground">Thanks — noted.</span>}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Still stuck?{' '}
          <Link to="/portal/report" className="text-brand hover:underline">
            Report an issue
          </Link>{' '}
          and the support team will pick it up.
        </p>
      </div>
    </article>
  );
}
